import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ExternalLink, Factory, FileText, Receipt } from "lucide-react";
import { BrandLockup } from "@/components/landing/LogoMark";
import { platformClient } from "./platformApi";

const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const ORDER_LABELS = { nieuw: "Nieuw", in_productie: "In productie", wacht_materiaal: "Wacht op materiaal", gereed: "Gereed" };
const DOC_LABELS = { concept: "Concept", verstuurd: "Verstuurd", akkoord: "Akkoord", afgewezen: "Afgewezen", betaald: "Betaald", verlopen: "Verlopen" };

const STATUS_STYLE = (s) =>
    ["akkoord", "betaald", "gereed"].includes(s)
        ? "border-gold/60 text-gold"
        : ["afgewezen", "verlopen"].includes(s)
          ? "border-red-400/30 text-red-300/70"
          : "border-white/20 text-ash";

export default function PortalPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await platformClient.get(`/public/portal/${token}`);
                setData(data);
            } catch {
                setError(true);
            }
        })();
    }, [token]);

    return (
        <div data-testid="portal-page" className="min-h-screen bg-ink px-6 py-12 text-bone">
            <div className="mx-auto max-w-3xl">
                <BrandLockup emblemClass="h-12 w-12" textClass="text-xl" align="start" />

                {error ? (
                    <div className="mt-16 border border-white/10 bg-coal p-10 text-center">
                        <p data-testid="portal-invalid" className="font-display text-2xl">Link ongeldig</p>
                        <p className="mt-3 text-sm text-ash">Vraag je contactpersoon om een nieuwe portaallink.</p>
                    </div>
                ) : !data ? (
                    <p className="mt-16 text-xs uppercase tracking-[0.3em] text-ash">Laden…</p>
                ) : (
                    <>
                        <div className="mt-10">
                            {data.sender?.logo_url ? (
                                <img
                                    data-testid="portal-tenant-logo"
                                    src={`${process.env.REACT_APP_BACKEND_URL}${data.sender.logo_url}`}
                                    alt={data.sender?.name}
                                    className="mb-6 max-h-14 max-w-52 object-contain"
                                />
                            ) : null}
                            <p className="text-[10px] uppercase tracking-[0.35em] text-gold">Klantportaal</p>
                            <h1 className="mt-3 font-display text-3xl tracking-tight md:text-4xl">
                                {data.contact.company}
                            </h1>
                            <p className="mt-2 text-sm text-ash">
                                Alles rond je opdrachten bij {data.sender?.name} — altijd actueel.
                            </p>
                        </div>

                        <section className="mt-10">
                            <p className="text-[10px] uppercase tracking-[0.3em] text-ash">
                                <Factory className="mr-2 inline h-3.5 w-3.5 text-gold" />Productieorders
                            </p>
                            <div className="mt-4 space-y-2">
                                {data.orders.length === 0 ? (
                                    <p className="border border-white/10 bg-coal p-6 text-sm text-ash">Geen lopende orders.</p>
                                ) : (
                                    data.orders.map((o) => (
                                        <div key={o.order_number} data-testid={`portal-order-${o.order_number}`} className="border border-white/10 bg-coal p-5">
                                            <div className="flex flex-wrap items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-sm text-bone"><span className="text-gold">{o.order_number}</span> — {o.description}</p>
                                                    <p className="mt-1 text-xs text-ash">
                                                        {o.quantity} stuks{o.due_date ? ` · ${o.due_date}` : ""}
                                                        {o.steps_total ? ` · ${o.steps_done}/${o.steps_total} stappen gereed` : ""}
                                                    </p>
                                                </div>
                                                <span className={`border px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE(o.status)}`}>
                                                    {ORDER_LABELS[o.status] || o.status}
                                                </span>
                                            </div>
                                            {o.steps_total ? (
                                                <div className="mt-3 h-1 w-full bg-white/5">
                                                    <div className="h-1 bg-gold transition-all" style={{ width: `${(o.steps_done / o.steps_total) * 100}%` }} />
                                                </div>
                                            ) : null}
                                        </div>
                                    ))
                                )}
                            </div>
                        </section>

                        <section className="mt-10">
                            <p className="text-[10px] uppercase tracking-[0.3em] text-ash">
                                <FileText className="mr-2 inline h-3.5 w-3.5 text-gold" />Offertes
                            </p>
                            <div className="mt-4 space-y-2">
                                {data.offertes.length === 0 ? (
                                    <p className="border border-white/10 bg-coal p-6 text-sm text-ash">Geen offertes.</p>
                                ) : (
                                    data.offertes.map((d) => (
                                        <a key={d.number} href={d.url} data-testid={`portal-offerte-${d.number}`}
                                            className="flex items-center justify-between gap-3 border border-white/10 bg-coal p-5 transition-colors hover:border-gold/40">
                                            <div>
                                                <p className="text-sm text-bone">{d.number} <span className="text-ash">· {d.issue_date}</span></p>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm text-gold">{euro(d.total)}</span>
                                                <span className={`border px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE(d.status)}`}>
                                                    {DOC_LABELS[d.status] || d.status}
                                                </span>
                                                <ExternalLink className="h-3.5 w-3.5 text-ash" />
                                            </div>
                                        </a>
                                    ))
                                )}
                            </div>
                        </section>

                        <section className="mt-10">
                            <p className="text-[10px] uppercase tracking-[0.3em] text-ash">
                                <Receipt className="mr-2 inline h-3.5 w-3.5 text-gold" />Facturen
                            </p>
                            <div className="mt-4 space-y-2">
                                {data.facturen.length === 0 ? (
                                    <p className="border border-white/10 bg-coal p-6 text-sm text-ash">Geen facturen.</p>
                                ) : (
                                    data.facturen.map((d) => (
                                        <a key={d.number} href={d.url} data-testid={`portal-factuur-${d.number}`}
                                            className="flex items-center justify-between gap-3 border border-white/10 bg-coal p-5 transition-colors hover:border-gold/40">
                                            <div>
                                                <p className="text-sm text-bone">{d.number} <span className="text-ash">· {d.issue_date}</span></p>
                                                {d.due_date ? <p className="mt-0.5 text-xs text-ash">Vervaldatum {d.due_date}</p> : null}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <span className="text-sm text-gold">{euro(d.total)}</span>
                                                <span className={`border px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${STATUS_STYLE(d.status)}`}>
                                                    {DOC_LABELS[d.status] || d.status}
                                                </span>
                                                <ExternalLink className="h-3.5 w-3.5 text-ash" />
                                            </div>
                                        </a>
                                    ))
                                )}
                            </div>
                        </section>

                        {data.sender?.details?.email || data.sender?.details?.phone ? (
                            <p className="mt-10 border-t border-white/10 pt-6 text-xs text-ash">
                                Vragen? {data.sender.details.email || ""} {data.sender.details.phone ? `· ${data.sender.details.phone}` : ""}
                            </p>
                        ) : null}
                    </>
                )}
                <p className="mt-8 text-center text-[10px] uppercase tracking-[0.3em] text-ash/50">
                    Verzonden via Clarke Business Solutions
                </p>
            </div>
        </div>
    );
}
